import { useRef, useState } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import Swiper from "react-id-swiper";

import CarouselArrow from "./CarouselArrow";

import "swiper/css/swiper.css";
import styles from "./Carousel.module.scss";

const defaultCarouselSettings = {
  slidesPerView: "auto",
  loop: true,
  speed: 500,
  keyboard: {
    enabled: true,
  },
};

// Main Carousel Component
function SwiperCarousel({
  className,
  carouselProps,
  children,
  nextArrowProps,
  prevArrowProps,
  onSlideIndexChange,
  onSwipe,
  fadingEdge,
  lazyLoad,
  badgeChildren,
}) {
  const container = useRef(null);

  const lazyLoadingSetting = {
    watchSlidesVisibility: true,
    lazy: {
      loadPrevNext: true,
      loadOnTransitionStart: true,
      elementClass: "swiper-lazy",
    },
  };

  const processEdge = (begin, end) => {
    if (container.current.classList?.toggle) {
      container.current.classList.toggle(styles.edge_begin, !begin);
      container.current.classList.toggle(styles.edge_end, !end);
    }
  };

  function checkEdge() {
    processEdge(this?.isBeginning === true, this?.isEnd === true);
  }

  const settings = {
    ...defaultCarouselSettings,
    loopedSlides: children.length,
    navigation: {},
    on: {},
    ...(lazyLoad ? lazyLoadingSetting : {}),
    ...carouselProps,
  };

  if (onSlideIndexChange) {
    settings.on.slideChange = function () {
      onSlideIndexChange?.(this.realIndex);
    };
  }

  if (onSwipe) {
    settings.on.touchEnd = function () {
      onSwipe?.(this.realIndex);
    };
  }

  // Callbacks to detect edge
  // We do not need these callbacks if carousel is circular, as isEnd and isBeginning are always false
  if (!settings.loop && fadingEdge) {
    settings.on = {
      ...settings.on,
      init: checkEdge,
      resize: checkEdge,
      fromEdge: checkEdge,
      reachBeginning: function () {
        processEdge(true, this?.isEnd === true);
      },
      reachEnd: function () {
        processEdge(this?.isBeginning === true, true);
      },
    };
  }

  if (prevArrowProps) {
    settings.navigation.prevEl = prevArrowProps.id
      ? `#${prevArrowProps.id}`
      : '[data-orientation="left"]';
  }

  if (nextArrowProps) {
    settings.navigation.nextEl = nextArrowProps.id
      ? `#${nextArrowProps.id}`
      : '[data-orientation="right"]';
  }

  const [isHoveringArrow, setIsHoveringArrow]=useState(false)

  const hoverEvents = {
    onMouseEnter: () => setIsHoveringArrow(true),
    onMouseLeave: () => setIsHoveringArrow(false),
  };

  return (
    <div
      className={classnames(
        styles.container,
        styles.full_width,
        settings.loop && styles.edge_begin,
        settings.loop && styles.edge_end,
        settings.centeredSlides && styles.centered_slide,
        fadingEdge && styles.fading_edge,
        className,
      )}
      ref={container}
    >
      {prevArrowProps && (
        <CarouselArrow
          active
          orientation="left"
          {...hoverEvents}
          {...prevArrowProps}
          className={classnames(
            prevArrowProps.className,
            styles.arrow_container_swiper,
          )}
        />
      )}
      {!!badgeChildren && (
        <div
          style={{ opacity: isHoveringArrow ? 0 : 1 }}
          className={styles.badge}
        >
          {badgeChildren}
        </div>
      )}
      <Swiper {...settings}>{children}</Swiper>
      {nextArrowProps && (
        <CarouselArrow
          active
          orientation="right"
          {...hoverEvents}
          {...nextArrowProps}
          className={classnames(
            nextArrowProps.className,
            styles.arrow_container_swiper,
          )}
        />
      )}
    </div>
  );
}

SwiperCarousel.propTypes = {
  className: PropTypes.string,
  carouselProps: PropTypes.object,
  children: PropTypes.node.isRequired,
  prevArrowProps: PropTypes.object,
  nextArrowProps: PropTypes.object,
  onSlideIndexChange: PropTypes.func,
  onSwipe: PropTypes.func,
  fadingEdge: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  badgeChildren: PropTypes.node,
};

SwiperCarousel.defaultProps = {
  carouselProps: {},
  prevArrowProps: null,
  nextArrowProps: null,
  onSlideIndexChange: null,
  onSwipe: null,
  fadingEdge: false,
  lazyLoad: true,
  badgeChildren: null,
};

export default SwiperCarousel;
